import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import PageHeader1 from "../../components/common/PageHeader1";
import toast, { Toaster } from "react-hot-toast";
import { usePermissions } from "../../context/AuthContext";

function SkillList() {
  const { can } = usePermissions();
  const [skills, setSkills] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [""],
  });

  const BASE_URL = process.env.REACT_APP_BASEURL;

  const fetchSkills = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const response = await axios.get(
        `${BASE_URL}/admin/skill?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      const data = response.data;
      setSkills(data?.data || []);
      setPagination(data?.paginationDetail || { totalDocs: 0, totalPages: 0 });
    } catch (error) {
      toast.error("Failed to load skills.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    fetchSkills(page + 1, pageSize);
  }, [paginationModel]);

  const handleClickOpen = (id) => {
    setSelectedUserId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    const token = localStorage.getItem("vedhvidhi-admin-token");
    try {
      await axios.delete(`${BASE_URL}/admin/skill/${selectedUserId}`, {
        headers: { Authorization: token },
      });

      toast.success("Skill deleted successfully!");

      const totalDocs = pagination.totalDocs - 1;
      const totalPages = Math.ceil(totalDocs / paginationModel.pageSize);
      const newPage = Math.max(0, Math.min(paginationModel.page, totalPages - 1));

      setPaginationModel((prev) => ({ ...prev, page: newPage }));
      fetchSkills(newPage + 1, paginationModel.pageSize);
    } catch (error) {
      toast.error("Failed to delete skill.");
    } finally {
      setDeleteLoading(false);
      handleClose();
    }
  };

  const canEdit = can('skills:edit');
  const canDelete = can('skills:delete');

  const columns = [
    { field: "sono", headerName: "S.NO", width: 150 },
    { field: "name", headerName: "Skill Name", width: 250 },
    { field: "createdAt", headerName: "Created At", width: 220 },
    { field: "updatedAt", headerName: "Updated At", width: 220 },
    ...(canEdit || canDelete
      ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 150,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) => (
            <Box display="flex" alignItems="center" gap={0.5}>
              {canEdit && (
                <Tooltip title="Edit Skill">
                  <IconButton component={Link} to={`/skill-edit/${row.id}`}>
                    <i className="icofont-edit text-info"></i>
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Skill">
                  <IconButton onClick={() => handleClickOpen(row.id)}>
                    <i className="icofont-ui-delete text-danger"></i>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ),
        },
      ]
      : []),
  ];


  const rows =
    skills?.map((skill, index) => ({
      sono: paginationModel.page * paginationModel.pageSize + index + 1,
      id: skill._id,
      name: skill.skillName,
      createdAt: skill.createdAt
        ? new Date(skill.createdAt).toLocaleString()
        : "",
      updatedAt: skill.updatedAt
        ? new Date(skill.updatedAt).toLocaleString()
        : "",
    })) || [];

  return (
    <div className="body d-flex py-1">
      <Toaster />
      <div className="container-xxl">
        <PageHeader1
          righttitle="Add Skill"
          link="/skill-add"
          routebutton={can('skills:add')}
          pagetitle="Skill List"
        />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ height: 500 }}>
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      loading={loading}
                      disableColumnFilter
                      disableDensitySelector
                      slots={{ toolbar: GridToolbar }}
                      filterModel={filterModel}
                      onFilterModelChange={(newModel) =>
                        setFilterModel(newModel)
                      }
                      slotProps={{ toolbar: { showQuickFilter: true } }}
                      columnVisibilityModel={columnVisibilityModel}
                      onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibilityModel(newModel)
                      }
                      disableRowSelectionOnClick
                      paginationMode="server"
                      rowCount={pagination?.totalDocs || 0}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title">Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this skill?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleDelete}
            disabled={deleteLoading}
            color="error"
          >
            {deleteLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SkillList;